import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { createProduct, getAdminProduct, getColors, getProducts, updateProduct } from '../../lib/api';
import { uploadImage } from '../../utils/imageUpload';

const generateSlug = (title) => {
  return title
    .toLowerCase()
    .replace(/ğ/g, 'g')
    .replace(/ü/g, 'u')
    .replace(/ş/g, 's')
    .replace(/ı/g, 'i')
    .replace(/ö/g, 'o')
    .replace(/ç/g, 'c')
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');
};

const CATEGORIES = [
  { id: 'deck', name: 'Deck' },
  { id: 'fence', name: 'Çit Sistemleri' },
  { id: 'garden_gate', name: 'Bahçe Kapı Sistemleri' },
  { id: 'facade', name: 'Cephe Sistemleri-Vinylbond' },
  { id: 'pergola', name: 'Pergola' },
  { id: 'baffle_ceiling', name: 'Baffle Tavan / Seperatör' },
  { id: 'flat_panel', name: 'Flat Paneller' },
];

const ProductForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [colors, setColors] = useState([]);
  const [galleryImages, setGalleryImages] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    main_image: '',
    faq_image: '',
    slug: '',
    category: '',
    display_order: 0,
    features: [],
    technical_specs: [],
    faq: [],
    is_active: true,
  });

  const [newFeature, setNewFeature] = useState({ title: '', description: '' });
  const [newTechSpec, setNewTechSpec] = useState({ title: '', value: '' });
  const [newFaq, setNewFaq] = useState({ question: '', answer: '' });

  const fetchColors = useCallback(async () => {
    try {
      const data = await getColors();
      setColors(data || []);
    } catch (error) {
      console.error('Error fetching colors:', error);
      toast.error('Renk listesi yüklenirken hata oluştu');
    }
  }, []);

  const fetchProduct = useCallback(async () => {
    try {
      const data = await getAdminProduct(id);
      setFormData({
        ...data,
        features: data.features || [],
        technical_specs: data.technical_specs || [],
        faq: data.faq || [],
      });
      setGalleryImages((data.product_images || []).map((img) => ({
        id: img.id,
        image_url: img.image_url,
        color_id: img.color_id || '',
      })));
    } catch (error) {
      console.error('Error fetching product:', error);
      toast.error(error.message || 'Ürün bilgileri yüklenirken hata oluştu');
    }
  }, [id]);

  useEffect(() => {
    fetchColors();
    if (id) {
      fetchProduct();
    }
  }, [id, fetchColors, fetchProduct]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const nextValue = type === 'checkbox' ? checked : value;

    if (name === 'title') {
      setFormData((prev) => ({
        ...prev,
        title: value,
        slug: prev.slug || generateSlug(value),
      }));
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: nextValue }));
  };

  const handleSingleImageUpload = async (file, field, successMessage) => {
    if (!file) return;
    try {
      setLoading(true);
      const imageUrl = await uploadImage(file, 'images', 'products');
      setFormData((prev) => ({ ...prev, [field]: imageUrl }));
      toast.success(successMessage);
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Görsel yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleGalleryImageUpload = async (file) => {
    if (!file) return;
    try {
      setLoading(true);
      const imageUrl = await uploadImage(file, 'images', 'products');
      setGalleryImages((prev) => [...prev, { image_url: imageUrl, color_id: '' }]);
      toast.success('Galeri görseli başarıyla yüklendi');
    } catch (error) {
      console.error('Error uploading gallery image:', error);
      toast.error('Galeri görseli yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleGalleryImageDelete = (index) => {
    setGalleryImages((prev) => prev.filter((_, i) => i !== index));
    toast.success('Galeri görseli başarıyla silindi');
  };

  const moveGalleryImage = (index, direction) => {
    const newImages = [...galleryImages];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= newImages.length) return;
    [newImages[index], newImages[newIndex]] = [newImages[newIndex], newImages[index]];
    setGalleryImages(newImages);
  };

  const handleColorSelect = (imageIndex, colorId) => {
    setGalleryImages((prev) => prev.map((img, i) => (i === imageIndex ? { ...img, color_id: colorId } : img)));
    toast.success('Renk başarıyla atandı');
  };

  const handleAddFeature = () => {
    if (newFeature.title && newFeature.description) {
      setFormData((prev) => ({ ...prev, features: [...prev.features, { ...newFeature }] }));
      setNewFeature({ title: '', description: '' });
    }
  };

  const handleRemoveFeature = (index) => {
    setFormData((prev) => ({ ...prev, features: prev.features.filter((_, i) => i !== index) }));
  };

  const handleAddTechSpec = () => {
    if (newTechSpec.title && newTechSpec.value) {
      setFormData((prev) => ({
        ...prev,
        technical_specs: [...prev.technical_specs, { ...newTechSpec }],
      }));
      setNewTechSpec({ title: '', value: '' });
    }
  };

  const handleRemoveTechSpec = (index) => {
    setFormData((prev) => ({
      ...prev,
      technical_specs: prev.technical_specs.filter((_, i) => i !== index),
    }));
  };

  const handleAddFaq = () => {
    if (newFaq.question && newFaq.answer) {
      setFormData((prev) => ({ ...prev, faq: [...prev.faq, { ...newFaq }] }));
      setNewFaq({ question: '', answer: '' });
    }
  };

  const handleRemoveFaq = (index) => {
    setFormData((prev) => ({ ...prev, faq: prev.faq.filter((_, i) => i !== index) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);

      let displayOrder = Number(formData.display_order || 0);
      if (!id && !displayOrder) {
        const products = await getProducts();
        const maxOrder = products.reduce((max, item) => Math.max(max, Number(item.display_order || 0)), 0);
        displayOrder = maxOrder + 1;
      }

      const payload = {
        title: formData.title,
        description: formData.description,
        main_image: formData.main_image,
        faq_image: formData.faq_image,
        slug: formData.slug,
        category: formData.category,
        display_order: displayOrder,
        features: formData.features,
        technical_specs: formData.technical_specs,
        faq: formData.faq,
        is_active: formData.is_active,
        gallery_images: galleryImages.map((img) => ({
          image_url: img.image_url,
          color_id: img.color_id || null,
        })),
      };

      if (id) {
        await updateProduct(id, payload);
        toast.success('Ürün başarıyla güncellendi');
      } else {
        await createProduct(payload);
        toast.success('Ürün başarıyla oluşturuldu');
      }

      navigate('/admin/products');
    } catch (error) {
      console.error('Error saving product:', error);
      toast.error(error.message || 'Ürün kaydedilirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <form onSubmit={handleSubmit} className="space-y-8">
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-xl font-semibold mb-4">Temel Bilgiler</h2>
          <div className="grid grid-cols-1 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700">Başlık</label>
              <input type="text" name="title" value={formData.title} onChange={handleInputChange} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm" required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Kategori</label>
              <select name="category" value={formData.category} onChange={handleInputChange} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm" required>
                <option value="">Kategori Seçin</option>
                {CATEGORIES.map((category) => <option key={category.id} value={category.id}>{category.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Açıklama</label>
              <textarea name="description" value={formData.description} onChange={handleInputChange} rows="4" className="mt-1 block w-full rounded-md border-gray-300 shadow-sm" required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">URL</label>
              <input type="text" name="slug" value={formData.slug} onChange={handleInputChange} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm" required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Sıralama</label>
              <input type="number" name="display_order" value={formData.display_order} onChange={handleInputChange} min="1" className="mt-1 block w-full rounded-md border-gray-300 shadow-sm" required />
            </div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <input type="checkbox" name="is_active" checked={formData.is_active} onChange={handleInputChange} />
              Aktif
            </label>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-xl font-semibold mb-4">Görseller</h2>
          <div className="mb-6">
            <h3 className="text-lg font-medium mb-2">Ana Görsel</h3>
            <div className="flex items-center gap-4">
              <input type="file" accept="image/*" onChange={(e) => handleSingleImageUpload(e.target.files?.[0], 'main_image', 'Ana görsel başarıyla yüklendi')} className="flex-1" disabled={loading} />
              {formData.main_image && <img src={formData.main_image} alt="Ana görsel" className="w-24 h-24 object-cover rounded" />}
            </div>
          </div>

          <div className="mb-6">
            <h3 className="text-lg font-medium mb-2">FAQ Görseli</h3>
            <div className="flex items-center gap-4">
              <input type="file" accept="image/*" onChange={(e) => handleSingleImageUpload(e.target.files?.[0], 'faq_image', 'FAQ görseli başarıyla yüklendi')} className="flex-1" disabled={loading} />
              {formData.faq_image && <img src={formData.faq_image} alt="FAQ görseli" className="w-24 h-24 object-cover rounded" />}
            </div>
          </div>

          <div>
            <h3 className="text-lg font-medium mb-2">Galeri</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {galleryImages.map((image, index) => (
                <div key={`${image.image_url}-${index}`} className="border rounded p-4 relative group">
                  <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button type="button" onClick={() => moveGalleryImage(index, 'up')} disabled={index === 0 || loading} className="p-1 bg-white rounded shadow">↑</button>
                    <button type="button" onClick={() => moveGalleryImage(index, 'down')} disabled={index === galleryImages.length - 1 || loading} className="p-1 bg-white rounded shadow">↓</button>
                  </div>
                  <img src={image.image_url} alt={`Galeri ${index + 1}`} className="w-full h-48 object-cover rounded mb-2" />
                  <div className="flex items-center gap-2">
                    <select value={image.color_id || ''} onChange={(e) => handleColorSelect(index, e.target.value)} className="flex-1 p-2 border rounded" disabled={loading}>
                      <option value="">Renk seçin</option>
                      {colors.map((color) => <option key={color.id} value={color.id}>{color.name}</option>)}
                    </select>
                    <button type="button" onClick={() => handleGalleryImageDelete(index)} className="p-2 text-red-600">Sil</button>
                  </div>
                </div>
              ))}
              <div className="border rounded p-4 flex items-center justify-center">
                <input type="file" accept="image/*" onChange={(e) => handleGalleryImageUpload(e.target.files?.[0])} disabled={loading} className="w-full" />
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-xl font-semibold mb-4">Özellikler</h2>
          <div className="mb-6 space-y-4">
            {formData.features.map((feature, index) => (
              <div key={index} className="flex items-start gap-4 p-4 border rounded">
                <div className="flex-1"><p className="font-medium">{feature.title}</p><p className="text-gray-600">{feature.description}</p></div>
                <button type="button" onClick={() => handleRemoveFeature(index)} className="text-red-600">Sil</button>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-1 gap-4">
            <input type="text" value={newFeature.title} onChange={(e) => setNewFeature((prev) => ({ ...prev, title: e.target.value }))} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm" placeholder="Özellik Başlığı" />
            <input type="text" value={newFeature.description} onChange={(e) => setNewFeature((prev) => ({ ...prev, description: e.target.value }))} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm" placeholder="Özellik Açıklaması" />
            <button type="button" onClick={handleAddFeature} className="w-full px-4 py-2 bg-green-600 text-white rounded">Özellik Ekle</button>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-xl font-semibold mb-4">Teknik Özellikler</h2>
          <div className="mb-6 space-y-4">
            {formData.technical_specs.map((spec, index) => (
              <div key={index} className="flex items-start gap-4 p-4 border rounded">
                <div className="flex-1"><p className="font-medium">{spec.title}</p><p className="text-gray-600">{spec.value}</p></div>
                <button type="button" onClick={() => handleRemoveTechSpec(index)} className="text-red-600">Sil</button>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-1 gap-4">
            <input type="text" value={newTechSpec.title} onChange={(e) => setNewTechSpec((prev) => ({ ...prev, title: e.target.value }))} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm" placeholder="Özellik Başlığı" />
            <input type="text" value={newTechSpec.value} onChange={(e) => setNewTechSpec((prev) => ({ ...prev, value: e.target.value }))} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm" placeholder="Değer" />
            <button type="button" onClick={handleAddTechSpec} className="w-full px-4 py-2 bg-green-600 text-white rounded">Teknik Özellik Ekle</button>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-xl font-semibold mb-4">Sık Sorulan Sorular</h2>
          <div className="mb-6 space-y-4">
            {formData.faq.map((item, index) => (
              <div key={index} className="flex items-start gap-4 p-4 border rounded">
                <div className="flex-1"><p className="font-medium">{item.question}</p><p className="text-gray-600">{item.answer}</p></div>
                <button type="button" onClick={() => handleRemoveFaq(index)} className="text-red-600">Sil</button>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-1 gap-4">
            <input type="text" value={newFaq.question} onChange={(e) => setNewFaq((prev) => ({ ...prev, question: e.target.value }))} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm" placeholder="Soru" />
            <textarea value={newFaq.answer} onChange={(e) => setNewFaq((prev) => ({ ...prev, answer: e.target.value }))} rows="3" className="mt-1 block w-full rounded-md border-gray-300 shadow-sm" placeholder="Cevap" />
            <button type="button" onClick={handleAddFaq} className="w-full px-4 py-2 bg-green-600 text-white rounded">Soru Ekle</button>
          </div>
        </div>

        <div className="flex justify-end space-x-4">
          <button type="button" onClick={() => navigate('/admin/products')} className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50" disabled={loading}>İptal</button>
          <button type="submit" className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700" disabled={loading}>
            {loading ? 'Kaydediliyor...' : id ? 'Güncelle' : 'Oluştur'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ProductForm;
